import React, { useState, useEffect } from 'react';
import apiClient from '../api';

function CategoryManager({ onCategorySelect, onCategoryCreated, onCategoryUpdated }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    color: '#58a6ff',
    icon: '📝'
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [manageCategoriesOpen, setManageCategoriesOpen] = useState(false);

  // 预定义的图标和颜色
  const availableIcons = [
    '📝', '📌', '📅', '📋', '📎', '🔖', '📁', '📂', '📊', '📈',
    '💼', '📚', '🎯', '⭐', '🔥', '💡', '🏷️', '✅', '❌', '❓'
  ];

  const colorOptions = [
    { name: '蓝色', value: '#0366d6' },
    { name: '绿色', value: '#28a745' },
    { name: '紫色', value: '#6f42c1' },
    { name: '橙色', value: '#f66a0a' },
    { name: '黄色', value: '#ffd33d' },
    { name: '红色', value: '#d73a49' },
    { name: '青色', value: '#1b7c83' },
    { name: '粉色', value: '#ea4aaa' }
  ];

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/categories');
      setCategories(response.data);
    } catch (err) {
      console.error('获取分类失败:', err);
      setError('获取分类失败，请刷新重试');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const response = await apiClient.post('/categories', formData);
      setCategories([response.data.category, ...categories]);
      setShowCreateModal(false);
      setFormData({ name: '', color: '#58a6ff', icon: '📝' });
      setSuccessMessage('分类创建成功！');
      
      if (onCategoryCreated) {
        onCategoryCreated(response.data.category);
      }
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('创建分类失败:', err);
      setError(err.response?.data?.error || '创建分类失败');
    }
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const response = await apiClient.put(`/categories/${editingCategory.id}`, formData);
      
      // 更新本地状态
      setCategories(categories.map(cat => 
        cat.id === editingCategory.id ? { ...cat, ...response.data.category } : cat
      ));
      
      setShowEditModal(false);
      setEditingCategory(null);
      setFormData({ name: '', color: '#58a6ff', icon: '📝' });
      setSuccessMessage('分类更新成功！');
      
      if (onCategoryUpdated) {
        onCategoryUpdated(response.data.category);
      }
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('更新分类失败:', err);
      setError(err.response?.data?.error || '更新分类失败');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('确定要删除这个分类吗？相关备忘录将变为未分类状态。')) {
      return;
    }
    
    try {
      await apiClient.delete(`/categories/${categoryId}`);
      setCategories(categories.filter(cat => cat.id !== categoryId));
      setSuccessMessage('分类删除成功！');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('删除分类失败:', err);
      setError(err.response?.data?.error || '删除分类失败');
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      color: category.color,
      icon: category.icon
    });
    setShowEditModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleIconSelect = (icon) => {
    setFormData(prev => ({
      ...prev,
      icon
    }));
  };

  // 处理管理分类按钮点击 - 显示管理界面
  const handleManageCategories = () => {
    setManageCategoriesOpen(true);
  };

  // 从 MemoEditor 调用时打开管理分类模态框
  const openManageCategories = () => {
    setManageCategoriesOpen(true);
  };

  const styles = {
    container: {
      backgroundColor: 'var(--color-bg-secondary)',
      borderRadius: '8px',
      padding: '20px',
      marginBottom: '20px'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px'
    },
    title: {
      fontSize: '18px',
      fontWeight: '600',
      color: 'var(--color-text-primary)',
      margin: 0
    },
    categoriesGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: '12px',
      marginBottom: '20px'
    },
    categoryCard: {
      backgroundColor: 'var(--color-bg-primary)',
      border: '1px solid var(--color-border-primary)',
      borderRadius: '8px',
      padding: '15px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      position: 'relative'
    },
    categoryCardSelected: {
      borderColor: 'var(--color-accent)',
      boxShadow: '0 0 0 2px rgba(88, 166, 255, 0.2)',
      backgroundColor: 'rgba(88, 166, 255, 0.05)'
    },
    categoryHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      marginBottom: '8px'
    },
    categoryIcon: {
      fontSize: '20px'
    },
    categoryName: {
      fontSize: '16px',
      fontWeight: '500',
      color: 'var(--color-text-primary)',
      margin: 0
    },
    categoryColor: {
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      border: '2px solid var(--color-border-primary)'
    },
    categoryCount: {
      fontSize: '12px',
      color: 'var(--color-text-tertiary)',
      marginTop: '5px'
    },
    actions: {
      position: 'absolute',
      top: '10px',
      right: '10px',
      display: 'flex',
      gap: '5px',
      opacity: 0,
      transition: 'opacity 0.2s ease'
    },
    actionButton: {
      background: 'var(--color-bg-secondary)',
      border: '1px solid var(--color-border-primary)',
      borderRadius: '4px',
      padding: '4px 8px',
      fontSize: '12px',
      cursor: 'pointer',
      color: 'var(--color-text-primary)'
    },
    editButton: {
      backgroundColor: 'var(--color-accent)',
      color: 'white',
      borderColor: 'var(--color-accent)'
    },
    deleteButton: {
      backgroundColor: 'var(--color-btn-danger)',
      color: 'white',
      borderColor: 'var(--color-btn-danger)'
    },
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      backdropFilter: 'blur(5px)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1001
    },
    modalContent: {
      backgroundColor: 'var(--color-bg-primary)',
      borderRadius: '12px',
      padding: '30px',
      width: '90%',
      maxWidth: '500px',
      boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
      border: '1px solid var(--color-border-primary)',
    },
    formGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '500',
      color: 'var(--color-text-primary)'
    },
    input: {
      width: '100%',
      padding: '10px',
      border: '1px solid var(--color-border-primary)',
      borderRadius: '6px',
      backgroundColor: 'var(--color-bg-secondary)',
      color: 'var(--color-text-primary)',
      fontSize: '14px'
    },
    colorGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '8px',
      marginTop: '8px'
    },
    colorOption: {
      width: '40px',
      height: '40px',
      borderRadius: '6px',
      cursor: 'pointer',
      border: '3px solid transparent',
      transition: 'all 0.2s ease'
    },
    colorOptionSelected: {
      borderColor: 'var(--color-text-primary)',
      transform: 'scale(1.1)'
    },
    iconGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(6, 1fr)',
      gap: '8px',
      marginTop: '8px'
    },
    iconOption: {
      fontSize: '20px',
      textAlign: 'center',
      padding: '10px',
      borderRadius: '6px',
      cursor: 'pointer',
      backgroundColor: 'var(--color-bg-secondary)',
      transition: 'all 0.2s ease'
    },
    iconOptionSelected: {
      backgroundColor: 'var(--color-accent)',
      color: 'white',
      transform: 'scale(1.1)'
    },
    buttonGroup: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '10px',
      marginTop: '20px'
    },
    button: {
      padding: '10px 20px',
      borderRadius: '6px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500'
    },
    submitButton: {
      backgroundColor: 'var(--color-btn-primary)',
      color: 'white'
    },
    cancelButton: {
      backgroundColor: 'var(--color-btn-secondary)',
      color: 'var(--color-text-primary)'
    },
    message: {
      padding: '10px',
      borderRadius: '6px',
      marginBottom: '15px',
      fontSize: '14px'
    },
    errorMessage: {
      backgroundColor: 'rgba(215, 58, 73, 0.1)',
      color: '#d73a49',
      border: '1px solid rgba(215, 58, 73, 0.2)'
    },
    successMessage: {
      backgroundColor: 'rgba(40, 167, 69, 0.1)',
      color: '#28a745',
      border: '1px solid rgba(40, 167, 69, 0.2)'
    }
  };

  return (
    <>
      <div style={styles.container}>
        <div style={styles.header}>
          <h3 style={styles.title}>📁 分类管理</h3>
          <button 
            onClick={() => setShowCreateModal(true)}
            style={{
              ...styles.button,
              ...styles.submitButton,
              padding: '8px 16px'
            }}
          >
            + 新建分类
          </button>
        </div>

        {successMessage && (
          <div style={{...styles.message, ...styles.successMessage}}>
            {successMessage}
          </div>
        )}

        {error && (
          <div style={{...styles.message, ...styles.errorMessage}}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid var(--color-border-primary)',
              borderTopColor: 'var(--color-accent)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 15px'
            }}></div>
            <p style={{ color: 'var(--color-text-secondary)' }}>加载分类中...</p>
          </div>
        ) : categories.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px', color: 'var(--color-text-tertiary)' }}>
            <p>还没有创建任何分类</p>
            <p>点击上方"新建分类"按钮开始创建</p>
          </div>
        ) : (
          <div style={styles.categoriesGrid}>
            {categories.map(category => (
              <div 
                key={category.id}
                style={{
                  ...styles.categoryCard,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.querySelector('.category-actions').style.opacity = 1;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.querySelector('.category-actions').style.opacity = 0;
                }}
                onClick={() => onCategorySelect && onCategorySelect(category)}
              >
                <div style={styles.categoryHeader}>
                  <span style={styles.categoryIcon}>{category.icon}</span>
                  <h4 style={styles.categoryName}>{category.name}</h4>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div 
                    style={{
                      ...styles.categoryColor,
                      backgroundColor: category.color
                    }}
                  />
                  <span style={styles.categoryCount}>
                    {category.memo_count || 0} 个备忘录
                  </span>
                </div>
                
                <div className="category-actions" style={styles.actions}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditCategory(category);
                    }}
                    style={{...styles.actionButton, ...styles.editButton}}
                  >
                    编辑
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteCategory(category.id);
                    }}
                    style={{...styles.actionButton, ...styles.deleteButton}}
                  >
                    删除
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 创建分类模态框 */}
      {showCreateModal && (
        <div style={styles.modalOverlay} onClick={() => setShowCreateModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, marginBottom: '20px', color: 'var(--color-text-primary)' }}>
              新建分类
            </h3>
            
            {error && (
              <div style={{...styles.message, ...styles.errorMessage, marginBottom: '15px'}}>
                {error}
              </div>
            )}

            <form onSubmit={handleCreateCategory}>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="name">分类名称 *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  placeholder="例如：工作、学习、生活..."
                  style={styles.input}
                  required
                  autoFocus
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>选择颜色</label>
                <div style={styles.colorGrid}>
                  {colorOptions.map((color, index) => (
                    <div
                      key={index}
                      style={{
                        ...styles.colorOption,
                        backgroundColor: color.value,
                        ...(formData.color === color.value ? styles.colorOptionSelected : {})
                      }}
                      onClick={() => setFormData(prev => ({ ...prev, color: color.value }))}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>选择图标</label>
                <div style={styles.iconGrid}>
                  {availableIcons.map((icon, index) => (
                    <div
                      key={index}
                      style={{
                        ...styles.iconOption,
                        ...(formData.icon === icon ? styles.iconOptionSelected : {})
                      }}
                      onClick={() => handleIconSelect(icon)}
                    >
                      {icon}
                    </div>
                  ))}
                </div>
              </div>

              <div style={styles.buttonGroup}>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  style={{...styles.button, ...styles.cancelButton}}
                >
                  取消
                </button>
                <button
                  type="submit"
                  style={{...styles.button, ...styles.submitButton}}
                >
                  创建分类
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 编辑分类模态框 */}
      {showEditModal && editingCategory && (
        <div style={styles.modalOverlay} onClick={() => setShowEditModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, marginBottom: '20px', color: 'var(--color-text-primary)' }}>
              编辑分类
            </h3>
            
            {error && (
              <div style={{...styles.message, ...styles.errorMessage, marginBottom: '15px'}}>
                {error}
              </div>
            )}

            <form onSubmit={handleUpdateCategory}>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="name">分类名称 *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  placeholder="例如：工作、学习、生活..."
                  style={styles.input}
                  required
                  autoFocus
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>选择颜色</label>
                <div style={styles.colorGrid}>
                  {colorOptions.map((color, index) => (
                    <div
                      key={index}
                      style={{
                        ...styles.colorOption,
                        backgroundColor: color.value,
                        ...(formData.color === color.value ? styles.colorOptionSelected : {})
                      }}
                      onClick={() => setFormData(prev => ({ ...prev, color: color.value }))}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>选择图标</label>
                <div style={styles.iconGrid}>
                  {availableIcons.map((icon, index) => (
                    <div
                      key={index}
                      style={{
                        ...styles.iconOption,
                        ...(formData.icon === icon ? styles.iconOptionSelected : {})
                      }}
                      onClick={() => handleIconSelect(icon)}
                    >
                      {icon}
                    </div>
                  ))}
                </div>
              </div>

              <div style={styles.buttonGroup}>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingCategory(null);
                  }}
                  style={{...styles.button, ...styles.cancelButton}}
                >
                  取消
                </button>
                <button
                  type="submit"
                  style={{...styles.button, ...styles.submitButton}}
                >
                  更新分类
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default CategoryManager;